/**
 * Modal "Informações do Pedido"
 *
 * Visão simples (padrão):
 *  - Nome do serviço
 *  - Se houver erros: destaque imediato dos objetos com problema
 *  - Se estiver OK: status verde curto
 *  - Resumo compacto de contadores (erro / alerta / ok)
 *
 * "Ver mais detalhes" (expandível inline):
 *  - Badges do serviço (corte, formato, DPI)
 *  - Barras de progresso de limites
 *  - Linha de configuração (objetos · unidade · contração)
 *  - Lista completa de objetos
 */

import React, { useState, useMemo } from 'react';
import {
  XIcon as X,
  ChevronDownIcon as ChevronDown,
  AlertTriangleIcon as AlertTriangle,
  InfoIcon as Info,
  CheckIcon as Check,
  CircleIcon as Circle,
} from '../../icons';
import { OrderObject, ProductRules } from '../types/objects';

interface DebugModalProps {
  globalContractionValue: string;
  selectedObjects: OrderObject[];
  hasMultipleContractions: boolean;
  productRules?: ProductRules;
  onClose: () => void;
  isMetros: boolean;
}

/* ---------- Subcomponentes ---------- */

const LimitProgressBar: React.FC<{
  label: string;
  current: number;
  max: number | null | undefined;
  unit?: string;
}> = ({ label, current, max, unit = 'cm' }) => {
  if (!max || max <= 0) return null;

  const percentage = Math.min((current / max) * 100, 100);
  const exceeds = current > max;
  const isClose = percentage >= 90 && !exceeds;

  const barColor = exceeds ? 'bg-red-500' : isClose ? 'bg-amber-500' : 'bg-emerald-500';
  const textColor = exceeds ? 'text-red-400' : isClose ? 'text-amber-400' : 'text-emerald-400';

  return (
    <div className="mb-3">
      <div className="flex justify-between items-center mb-1">
        <span className="text-xs text-gray-400">{label}</span>
        <span className={`text-xs font-medium ${textColor}`}>
          {current.toFixed(1)} / {max} {unit}
          {exceeds && <span className="ml-1 text-red-500">⚠️ Excede!</span>}
        </span>
      </div>
      <div className="h-2 bg-navy-900 rounded-full overflow-hidden">
        <div
          className={`h-full ${barColor} transition-all duration-300`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
      <div className="flex justify-between items-center mt-0.5">
        <span className="text-[10px] text-gray-500">0</span>
        <span className="text-[10px] text-gray-500">{percentage.toFixed(0)}%</span>
        <span className="text-[10px] text-gray-500">{max} {unit}</span>
      </div>
    </div>
  );
};

const ErrorObjectCard: React.FC<{
  obj: OrderObject;
  maxWidth?: number | null;
  maxHeight?: number | null;
}> = ({ obj, maxWidth, maxHeight }) => {
  const widthExceeds = maxWidth && obj.width > maxWidth;
  const heightExceeds = maxHeight && obj.height > maxHeight;
  const widthExcess = widthExceeds ? (obj.width - maxWidth!).toFixed(1) : null;
  const heightExcess = heightExceeds ? (obj.height - maxHeight!).toFixed(1) : null;

  return (
    <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg mb-2">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center">
          <Circle className="w-4 h-4 text-red-500" />
        </div>
        <div>
          <span className="text-sm font-medium text-white block">{obj.nome}</span>
          <span className="text-xs text-gray-400">Página {obj.pageIndex ?? '-'}</span>
        </div>
      </div>

      <div className="mt-2 grid grid-cols-2 gap-2">
        <div className={`p-2 rounded ${widthExceeds ? 'bg-red-500/20' : 'bg-navy-700/50'}`}>
          <span className="text-[10px] text-gray-400 block">Largura</span>
          <span className={`text-sm font-mono ${widthExceeds ? 'text-red-400' : 'text-white'}`}>
            {obj.width} cm
          </span>
          {widthExceeds && (
            <span className="text-[10px] text-red-400 block">+{widthExcess} cm acima do limite</span>
          )}
        </div>
        <div className={`p-2 rounded ${heightExceeds ? 'bg-red-500/20' : 'bg-navy-700/50'}`}>
          <span className="text-[10px] text-gray-400 block">Altura</span>
          <span className={`text-sm font-mono ${heightExceeds ? 'text-red-400' : 'text-white'}`}>
            {obj.height} cm
          </span>
          {heightExceeds && (
            <span className="text-[10px] text-red-400 block">+{heightExcess} cm acima do limite</span>
          )}
        </div>
      </div>
    </div>
  );
};

/* ---------- Componente principal ---------- */

export const DebugModal: React.FC<DebugModalProps> = ({
  globalContractionValue,
  selectedObjects,
  hasMultipleContractions,
  productRules,
  onClose,
  isMetros,
}) => {
  const objectStats = useMemo(
    () => ({
      ok: selectedObjects.filter(o => o.status === 'success').length,
      warning: selectedObjects.filter(o => o.status === 'warning').length,
      error: selectedObjects.filter(o => o.status === 'error').length,
    }),
    [selectedObjects],
  );

  const errorObjects = useMemo(
    () => selectedObjects.filter(o => o.status === 'error'),
    [selectedObjects],
  );

  const maxObjectDimensions = useMemo(() => {
    if (selectedObjects.length === 0) return { width: 0, height: 0 };
    return {
      width: Math.max(...selectedObjects.map(o => o.width)),
      height: Math.max(...selectedObjects.map(o => o.height)),
    };
  }, [selectedObjects]);

  const [showDetails, setShowDetails] = useState(false);

  const hasErrors = objectStats.error > 0;
  const allOk = objectStats.ok > 0 && objectStats.error === 0 && objectStats.warning === 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
      <div className="bg-navy-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-fadeIn">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b border-navy-700">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-white truncate">
              {productRules?.nome_servico || 'Informações do Pedido'}
            </h2>
            <span className="text-xs text-gray-400">
              {selectedObjects.length} objeto{selectedObjects.length !== 1 ? 's' : ''} ·{' '}
              {isMetros ? 'Metros' : 'Unidades'}
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors rounded-lg p-2 hover:bg-navy-700 flex-shrink-0"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 scrollbar-thin scrollbar-thumb-navy-600 scrollbar-track-navy-900">
          {/* ====== VISÃO SIMPLES ====== */}

          {/* Erros — destaque imediato */}
          {hasErrors && (
            <div className="mb-4 p-4 bg-red-500/15 border-2 border-red-400/60 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Circle className="w-5 h-5 text-red-500" />
                <span className="text-sm font-bold text-red-400">
                  {objectStats.error} objeto{objectStats.error > 1 ? 's' : ''} com erro
                </span>
              </div>
              <p className="text-xs text-red-300/80 mb-3">
                Corrija antes de salvar — excedem os limites do serviço:
              </p>
              {errorObjects.map(obj => (
                <ErrorObjectCard
                  key={obj.id}
                  obj={obj}
                  maxWidth={productRules?.largura_maxima_cm}
                  maxHeight={productRules?.altura_maxima_cm}
                />
              ))}
            </div>
          )}

          {/* Status OK — curto */}
          {!hasErrors && allOk && (
            <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg flex items-center gap-2">
              <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
              <span className="text-sm text-emerald-400">
                Todos os {objectStats.ok} objetos estão dentro dos limites
              </span>
            </div>
          )}

          {/* Alertas rápidos (sem erro) */}
          {!hasErrors && objectStats.warning > 0 && (
            <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-sm font-medium text-amber-400 block">
                  {objectStats.warning} objeto{objectStats.warning > 1 ? 's' : ''} com alerta
                </span>
                <span className="text-xs text-amber-300/70">
                  Largura muito pequena (menor ou igual a 10cm)
                </span>
              </div>
            </div>
          )}

          {/* Resumo compacto de contadores */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className={`p-3 rounded-lg text-center ${
              objectStats.error > 0
                ? 'bg-red-500/25 border border-red-400/50'
                : 'bg-navy-700/50 border border-navy-600'
            }`}>
              <span className={`text-xl font-bold block ${objectStats.error > 0 ? 'text-red-400' : 'text-gray-400'}`}>
                {objectStats.error}
              </span>
              <span className="text-[11px] text-gray-400">Erros</span>
            </div>
            <div className={`p-3 rounded-lg text-center ${
              objectStats.warning > 0
                ? 'bg-amber-500/20 border border-amber-500/50'
                : 'bg-navy-700/50 border border-navy-600'
            }`}>
              <span className={`text-xl font-bold block ${objectStats.warning > 0 ? 'text-amber-400' : 'text-gray-400'}`}>
                {objectStats.warning}
              </span>
              <span className="text-[11px] text-gray-400">Alertas</span>
            </div>
            <div className={`p-3 rounded-lg text-center ${
              objectStats.ok > 0
                ? 'bg-emerald-500/20 border border-emerald-500/50'
                : 'bg-navy-700/50 border border-navy-600'
            }`}>
              <span className={`text-xl font-bold block ${objectStats.ok > 0 ? 'text-emerald-400' : 'text-gray-400'}`}>
                {objectStats.ok}
              </span>
              <span className="text-[11px] text-gray-400">OK</span>
            </div>
          </div>

          {/* ====== BOTÃO VER MAIS DETALHES ====== */}
          <button
            onClick={() => setShowDetails(v => !v)}
            className="w-full flex items-center justify-between px-3 py-2.5 bg-navy-700 hover:bg-navy-600 rounded-lg text-sm text-gray-300 hover:text-white transition-colors mb-3"
          >
            <span className="flex items-center gap-2">
              <Info className="w-4 h-4 text-blue-400" />
              Ver mais detalhes
            </span>
            <ChevronDown
              className={`w-4 h-4 text-gray-400 transition-transform ${showDetails ? 'rotate-180' : ''}`}
            />
          </button>

          {/* ====== DETALHES (expansível) ====== */}
          {showDetails && (
            <div className="space-y-3 animate-fadeIn">
              {/* Badges do serviço */}
              {productRules && (
                <div className="flex flex-wrap gap-2">
                  <div className="px-2.5 py-1.5 bg-indigo-500/20 rounded-lg text-xs text-indigo-300 font-medium">
                    {productRules.nome_servico}
                  </div>
                  <div className="px-2.5 py-1.5 bg-navy-600 rounded-lg text-xs text-gray-300">
                    🕐 Corte: {productRules.horario_corte}
                  </div>
                  <div className="px-2.5 py-1.5 bg-navy-600 rounded-lg text-xs text-gray-300">
                    📄 {productRules.exportacao?.formato || 'PNG'}
                  </div>
                  <div className="px-2.5 py-1.5 bg-navy-600 rounded-lg text-xs text-gray-300">
                    🔍 {productRules.exportacao?.resolucao_dpi || 300} DPI
                  </div>
                </div>
              )}

              {/* Linha de configuração */}
              <div className="text-xs text-gray-400">
                {selectedObjects.length} objeto{selectedObjects.length !== 1 ? 's' : ''} ·{' '}
                {isMetros ? 'Metros' : 'Unidades'} · Contração: {globalContractionValue}
                {hasMultipleContractions && <span className="text-amber-400"> · Múltiplas</span>}
              </div>

              {/* Barras de progresso de limites */}
              {productRules && (
                <div className="p-3 bg-navy-900/50 rounded-lg">
                  <h4 className="text-xs font-medium text-gray-400 mb-3 uppercase tracking-wide">
                    📐 Uso vs. Limites Máximos
                  </h4>
                  <LimitProgressBar
                    label="Largura"
                    current={maxObjectDimensions.width}
                    max={productRules.largura_maxima_cm}
                  />
                  <LimitProgressBar
                    label="Altura"
                    current={maxObjectDimensions.height}
                    max={productRules.altura_maxima_cm}
                  />
                </div>
              )}

              {/* Lista completa de objetos */}
              <div>
                <div className="mb-2">
                  <div className="px-2.5 py-1 inline-block bg-blue-500/20 rounded-lg text-xs text-blue-400 font-medium">
                    {selectedObjects.length} objetos no total
                  </div>
                </div>
                <div className="max-h-60 overflow-y-auto rounded-lg border border-navy-600">
                  <table className="min-w-full">
                    <thead className="bg-navy-700 sticky top-0">
                      <tr>
                        <th className="p-2.5 text-left text-xs text-gray-300 font-medium">Nome</th>
                        <th className="p-2.5 text-left text-xs text-gray-300 font-medium">Largura</th>
                        <th className="p-2.5 text-left text-xs text-gray-300 font-medium">Altura</th>
                        <th className="p-2.5 text-left text-xs text-gray-300 font-medium">Qtd</th>
                        <th className="p-2.5 text-left text-xs text-gray-300 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedObjects.map(obj => (
                        <tr
                          key={obj.id}
                          className={`border-t border-navy-600 hover:bg-navy-700/50 ${
                            obj.status === 'error' ? 'bg-red-500/15' : ''
                          }`}
                        >
                          <td className="p-2.5 text-xs text-white">
                            <div className="flex items-center gap-2">
                              <span className="truncate max-w-[150px]" title={obj.nome}>
                                {obj.nome}
                              </span>
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-navy-600 text-gray-400">
                                P{obj.pageIndex ?? '-'}
                              </span>
                            </div>
                          </td>
                          <td
                            className={`p-2.5 text-xs font-mono ${
                              productRules?.largura_maxima_cm &&
                              obj.width > productRules.largura_maxima_cm
                                ? 'text-red-400'
                                : 'text-gray-300'
                            }`}
                          >
                            {obj.width}cm
                          </td>
                          <td
                            className={`p-2.5 text-xs font-mono ${
                              productRules?.altura_maxima_cm &&
                              obj.height > productRules.altura_maxima_cm
                                ? 'text-red-400'
                                : 'text-gray-300'
                            }`}
                          >
                            {obj.height}cm
                          </td>
                          <td className="p-2.5 text-xs text-gray-300 font-mono">{obj.quantity}</td>
                          <td className="p-2.5 text-xs">
                            <span
                              className={`px-2 py-1 rounded-full text-[10px] font-medium ${
                                obj.status === 'success'
                                  ? 'bg-emerald-500/20 text-emerald-400'
                                  : obj.status === 'warning'
                                    ? 'bg-amber-500/20 text-amber-400'
                                    : 'bg-red-500/20 text-red-400'
                              }`}
                            >
                              {obj.status === 'success'
                                ? 'OK'
                                : obj.status === 'warning'
                                  ? 'Alerta'
                                  : 'Erro'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-navy-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2.5 bg-navy-600 text-white rounded-lg hover:bg-navy-500 transition-colors font-medium"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};
